/**
 * Socket.IO client singleton pour le temps-réel Nudge.
 *
 * - Connexion à la même origine (nginx proxifie /socket.io/ vers le backend 3001).
 * - Auth via le JWT existant (handshake auth.token).
 * - Reconnexion automatique.
 * - getSocketId() est utilisé par l'intercepteur axios pour poser le header `x-socket-id`
 *   → le backend exclut ce socket de la diffusion (anti-écho : on ne reçoit pas ses propres events).
 */

import { io, Socket } from 'socket.io-client';
import { getToken } from './api';
import { logger } from './logger';

let socket: Socket | null = null;
let visibilityHooked = false;

export function initSocket(): Socket {
  if (socket) return socket;

  socket = io({
    path: '/socket.io/',
    auth: { token: getToken() },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 500,
    reconnectionDelayMax: 8000,
    timeout: 10000,
  });

  socket.on('connect', () => logger.log('[socket] connecté', socket?.id));
  socket.on('disconnect', (reason) => logger.log('[socket] déconnecté', reason));
  socket.on('connect_error', (err) => logger.log('[socket] erreur connexion', err.message));

  // Mobile/PWA : au retour au premier plan (déverrouillage, switch d'app), l'OS a souvent
  // suspendu le socket. On force une reconnexion immédiate si déconnecté (sinon on attend
  // le backoff). L'event 'reconnect' déclenchera le refetch de rattrapage (useRealtimeSync).
  if (typeof document !== 'undefined' && !visibilityHooked) {
    visibilityHooked = true;
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && socket && !socket.connected) {
        logger.log('[socket] retour foreground → reconnexion');
        socket.connect();
      }
    });
  }

  return socket;
}

export function getSocket(): Socket | null {
  return socket;
}

export function getSocketId(): string | undefined {
  return socket?.connected ? socket.id : undefined;
}

/** Met à jour le token (ex: après refresh) et reconnecte proprement. */
export function reconnectSocketWithToken(): void {
  if (!socket) { initSocket(); return; }
  socket.auth = { token: getToken() } as any;
  socket.disconnect().connect();
}

export function disconnectSocket(): void {
  if (socket) {
    socket.disconnect();
    socket = null;
  }
}
